import { Overlay, OverlayRef } from "@angular/cdk/overlay";
import { ComponentPortal } from "@angular/cdk/portal";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Injectable,
  Input,
  Output,
} from "@angular/core";
import { Observable, Subject } from "rxjs";


/** Subset of MatSnackBarConfig the app uses. */
export interface BvToastConfig {
  duration?: number;
  panelClass?: string | string[];
  /** Accepted for parity with MatSnackBarConfig; maps to aria-live. */
  politeness?: "polite" | "assertive" | "off";
}

/** Handle to an open toast. Mirrors the slice of MatSnackBarRef the app uses. */
export class BvToastRef {
  private readonly _action = new Subject<void>();
  private readonly _dismissed = new Subject<void>();
  private isDismissed = false;

  constructor(
    private readonly overlayRef: OverlayRef,
    private readonly onClose: () => void
  ) {}

  /** @internal — called by the toast component when its action is clicked. */
  _fireAction(): void {
    this._action.next();
  }

  onAction(): Observable<void> {
    return this._action.asObservable();
  }

  afterDismissed(): Observable<void> {
    return this._dismissed.asObservable();
  }

  dismiss(): void {
    if (this.isDismissed) return;
    this.isDismissed = true;
    this.overlayRef.dispose();
    this._action.complete();
    this._dismissed.next();
    this._dismissed.complete();
    this.onClose();
  }
}

@Component({
    selector: "bv-toast",
    template: `
    <span class="bv-toast__message">{{ message }}</span>
    @if (action) {
      <button
        type="button"
        class="bv-toast__action"
        (click)="actionClick.emit()"
        >
        {{ action }}
      </button>
    }
    `,
    styleUrls: ["./toast.component.scss"],
    host: {
        class: "bv-toast",
        role: "status",
        "aria-live": "polite",
    },
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: []
})
export class BvToastComponent {
  @Input() message = "";
  @Input() action?: string;
  @Output() actionClick = new EventEmitter<void>();
}

/**
 * Toast notifications. Mirrors MatSnackBar.open(message, action?, config?).
 * One toast at a time (opening a new one dismisses the current).
 */
@Injectable({ providedIn: "root" })
export class BvToastService {
  private current: BvToastRef | null = null;
  private openCount = 0;
  /** Emits on every open and dismiss — lets consumers react to toast presence. */
  readonly changes$ = new Subject<void>();

  constructor(private readonly overlay: Overlay) {}

  open(
    message: string,
    action?: string,
    config: BvToastConfig = {}
  ): BvToastRef {
    this.current?.dismiss();

    const panelClass = ["bv-toast-panel"];
    if (config.panelClass) {
      panelClass.push(
        ...(Array.isArray(config.panelClass)
          ? config.panelClass
          : [config.panelClass])
      );
    }

    const overlayRef = this.overlay.create({
      positionStrategy: this.overlay
        .position()
        .global()
        .centerHorizontally()
        .bottom("24px"),
      panelClass,
      hasBackdrop: false,
      scrollStrategy: this.overlay.scrollStrategies.noop(),
    });

    const ref = new BvToastRef(overlayRef, () => {
      this.openCount = Math.max(0, this.openCount - 1);
      if (this.current === ref) this.current = null;
      this.changes$.next();
    });

    const compRef = overlayRef.attach(new ComponentPortal(BvToastComponent));
    compRef.instance.message = message;
    compRef.instance.action = action || undefined;
    compRef.instance.actionClick.subscribe(() => {
      ref._fireAction();
      ref.dismiss();
    });

    this.current = ref;
    this.openCount++;
    this.changes$.next();

    if (config.duration && config.duration > 0) {
      setTimeout(() => ref.dismiss(), config.duration);
    }

    return ref;
  }

  dismiss(): void {
    this.current?.dismiss();
  }

  hasOpen(): boolean {
    return this.openCount > 0;
  }
}
